// Product Feed Health — Feed Health section, deterministic check (Merchant API).
// Disapproved/demoted rollup + the most common serving-impacting item-level issues.

import { count } from '../../utils/format';
import type { EvidenceProps } from './registry';

interface IssueRow {
  code: string;
  severity: string;
  description: string;
  attribute: string | null;
  documentation: string | null;
  count: number;
}

interface FeedHealthShape {
  merchantId: string;
  productsReviewed: number;
  truncated: boolean;
  disapprovedCount: number;
  demotedCount: number;
  disapprovedPct: number;
  topIssues: IssueRow[];
  disapprovedSamples: { offerId: string; title: string | null; feedLabel: string | null }[];
}

export const ProductFeedHealthEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as FeedHealthShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Feed health
          <span className='evidence__section-tag evidence__section-tag--context'>
            Merchant Center {e.merchantId}
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {count(e.productsReviewed)}
              {e.truncated ? '+' : ''}
            </div>
            <div className='audience-coverage__lbl'>Products reviewed</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{count(e.disapprovedCount)}</div>
            <div className='audience-coverage__lbl'>Disapproved</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{count(e.demotedCount)}</div>
            <div className='audience-coverage__lbl'>Demoted</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{Math.round(e.disapprovedPct * 100)}%</div>
            <div className='audience-coverage__lbl'>Disapproval rate</div>
          </div>
        </div>
      </section>

      {e.topIssues.length > 0 && (
        <section className='evidence__section'>
          <h4 className='evidence__section-title'>Most common serving-impacting issues</h4>
          <table className='evidence__table evidence__table--feed-health'>
            <thead>
              <tr>
                <th>Issue</th>
                <th>Attribute</th>
                <th>Impact</th>
                <th className='evidence__th--right'>Products</th>
              </tr>
            </thead>
            <tbody>
              {e.topIssues.map(i => (
                <tr key={`${i.code}-${i.severity}`}>
                  <td>
                    {i.documentation ? (
                      <a href={i.documentation} target='_blank' rel='noreferrer'>
                        {i.description}
                      </a>
                    ) : (
                      i.description
                    )}
                  </td>
                  <td>{i.attribute ?? '—'}</td>
                  <td>
                    <span
                      className={`evidence__strength ${
                        i.severity === 'DISAPPROVED'
                          ? 'evidence__strength--average'
                          : 'evidence__strength--good'
                      }`}
                    >
                      {i.severity === 'DISAPPROVED' ? 'Disapproved' : 'Demoted'}
                    </span>
                  </td>
                  <td className='evidence__td--right'>{count(i.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {e.disapprovedSamples.length > 0 && (
        <section className='evidence__section'>
          <h4 className='evidence__section-title'>
            Sample disapproved products
            <span className='evidence__section-tag evidence__section-tag--context'>
              {e.disapprovedSamples.length} shown
            </span>
          </h4>
          <table className='evidence__table'>
            <thead>
              <tr>
                <th>Offer ID</th>
                <th>Title</th>
                <th>Feed</th>
              </tr>
            </thead>
            <tbody>
              {e.disapprovedSamples.map(s => (
                <tr key={s.offerId}>
                  <td>{s.offerId}</td>
                  <td>{s.title ?? '—'}</td>
                  <td>{s.feedLabel ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <p className='evidence__footnote'>
        Sourced from the Merchant API. <strong>Disapproved</strong> products can't serve at all;{' '}
        <strong>demoted</strong> products still serve but rank worse. Item-level issues are grouped
        by cause so systemic feed problems (a missing attribute across many products, price
        mismatches, etc.) surface ahead of one-off cases.
      </p>
    </div>
  );
};
